import { useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { arrow, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react-dom'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import FormatCurrency from 'components/Common/FormatCurrency'
import zIndex from 'styles/tools/z-index'
import type { ArenaSeatAnchor } from './arenaSeatPresentation'
import type { ArenaSelectedSeat } from './arenaSeatSelectionUtils'
import { getArenaDisplaySeatPrice, isBackendAvailabilityExplanation } from './arenaSeatSelectionUtils'

const Card = styled.div<{ $colour?: string }>`
  width: ${rem(220)};
  max-width: calc(100vw - ${rem(24)});
  padding: ${rem(8)} ${rem(12)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-top: ${rem(3)} solid ${props => props.$colour ?? 'transparent'};
  border-radius: ${props => props.theme.borderRadius.S};
  box-shadow: ${props => props.theme.shadow.bottom.medium};
  z-index: ${zIndex.tooltip};
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(8)};
`

const Arrow = styled.span<{ $colour?: string, $above: boolean }>`
  position: absolute;
  width: ${rem(20)};
  height: ${rem(10)};
  background: ${props => props.$colour ?? 'transparent'};
  clip-path: polygon(50% 0, 100% 100%, 0 100%);
  pointer-events: none;
  ${props => props.$above ? `bottom: ${rem(-10)}; transform: rotate(180deg);` : `top: ${rem(-13)};`}
`

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  margin-top: ${rem(4)};

  & + & {
    margin-top: ${rem(12)};
  }
`

const Marker = styled.span<{ $colour?: string, $restricted?: boolean, $large?: boolean }>`
  width: ${props => rem(props.$large ? 24 : 16)};
  height: ${props => rem(props.$large ? 24 : 16)};
  flex-shrink: 0;
  border-radius: 50%;
  background-color: ${props => props.$colour ?? 'transparent'};
  background-image: ${props => props.$restricted ? 'linear-gradient(to right, #0003 50%, transparent 50%)' : 'none'};
  border: ${props => props.$restricted ? '1px solid #0003' : 'none'};
`

interface Props {
  anchor: ArenaSeatAnchor
  seat: ArenaSelectedSeat
  selectedTimeSlot: App.ExperienceAvailabilityTimeSlot
  currency: string
  restrictedViewLabel: string
  onClose: () => void
  onEnter: () => void
  onLeave: () => void
}

function ArenaSeatHoverCard(props: Props) {
  const { anchor, seat, selectedTimeSlot, currency, restrictedViewLabel, onClose, onEnter, onLeave } = props
  const intl = useIntl()
  const closeLabel = intl.formatMessage({
    id: 'evqezJ5um5H46GWTaekH',
    defaultMessage: 'Close seat details',
  })
  const label = [seat.label ?? seat.seatId, seat.ticketTypeDesc ?? seat.seatBlock?.desc].filter(Boolean).join(' · ')
  const price = getArenaDisplaySeatPrice(seat, selectedTimeSlot)
  const note = !seat.isRestrictedView && !isBackendAvailabilityExplanation(seat.restrictedViewText) ? seat.restrictedViewText : undefined
  const arrowRef = useRef<HTMLSpanElement>(null)
  const middleware = useMemo(() => [offset(12), flip({ padding: 12 }), shift({ padding: 12 }), arrow({ element: arrowRef, padding: 8 })], [])
  const { refs, floatingStyles, middlewareData, placement, update } = useFloating({ placement: 'bottom', strategy: 'fixed', middleware })
  const arrowStyles = useMemo(() => ({ left: middlewareData.arrow?.x }), [middlewareData.arrow?.x])

  useEffect(() => {
    refs.setReference(anchor.element)
    if (refs.floating.current) return autoUpdate(anchor.element, refs.floating.current, update)
  }, [anchor.element, refs, update])

  useEffect(() => {
    function dismiss(event: Event) {
      if (event instanceof KeyboardEvent && event.key !== 'Escape') return
      if (event.type === 'pointerdown' && (event.composedPath().includes(anchor.element) || event.composedPath().includes(refs.floating.current!))) return
      onClose()
    }
    document.addEventListener('keydown', dismiss)
    document.addEventListener('pointerdown', dismiss)
    return () => {
      document.removeEventListener('keydown', dismiss)
      document.removeEventListener('pointerdown', dismiss)
    }
  }, [anchor.element, onClose, refs.floating])

  return createPortal(
    <Card ref={refs.setFloating} style={floatingStyles} $colour={seat.bgColor} role="dialog" aria-label={label} data-testid="arena-seat-hover-card" onPointerEnter={onEnter} onPointerLeave={onLeave} onFocus={onEnter} onBlur={onLeave}>
      <Arrow ref={arrowRef} style={arrowStyles} $colour={seat.bgColor} $above={placement.startsWith('top')} aria-hidden="true" />
      <Header>
        <BodyText variant="medium">{label}</BodyText>
        <IconButton kind="tertiary" size="small" aria-label={closeLabel} onClick={onClose}>
          <LineTimesIcon />
        </IconButton>
      </Header>
      {seat.isRestrictedView && <DetailRow>
        <Marker $colour={seat.bgColor} $restricted $large aria-hidden="true" />
        <BodyText variant="large">{restrictedViewLabel}</BodyText>
      </DetailRow>}
      {price !== undefined && <DetailRow>
        <Marker $colour={seat.bgColor} $large={seat.isRestrictedView} aria-hidden="true" />
        <BodyText variant="large" weight="bold"><FormatCurrency value={price} currency={currency} /></BodyText>
      </DetailRow>}
      {note && <BodyText variant="small" colour="neutral-two">{note}</BodyText>}
    </Card>,
    document.body,
  )
}

export default ArenaSeatHoverCard
